import { useCallback, useEffect, useId, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { Plus } from 'lucide-react';

import type { VisitTypeId, VisitTypeOption } from '../services/api/types.gen';
import { getClinicianDisplayName } from '../utils/clinicianUtils';
import { TaskTypeCombobox } from './task/TaskTypeCombobox';
import {
  createDefaultTaskTypeFilter,
  type TaskTypeFilterValue,
} from './task/taskTypeFilters';

export interface CalendarCreateVisitClinician {
  id: string;
  user_first_name: string;
  user_last_name: string;
  user_email?: string | null;
  clinician_first_name?: string | null;
  clinician_last_name?: string | null;
  clinician_credentials?: string | null;
}

/**
 * Returns true only after the visit has been created successfully.
 * Return false after any validation/API failure so the dialog stays open.
 */
export type CalendarCreateVisitHandler = (
  date: Date,
  visitType: VisitTypeId,
  clinicianId?: string
) => Promise<boolean> | boolean;

interface CalendarCreateVisitDialogProps {
  open: boolean;
  /**
   * Controlled dialog callback. Parent components must reflect the requested
   * value back through `open`; field state resets after `open` becomes false.
   */
  onOpenChange: (open: boolean) => void;
  selectedDate?: Date;
  visitTypeOptions: VisitTypeOption[];
  clinicians: CalendarCreateVisitClinician[];
  duplicateClinicianNames?: Set<string>;
  onCreateVisit: CalendarCreateVisitHandler;
}

export const CalendarCreateVisitDialog = ({
  open,
  onOpenChange,
  selectedDate,
  visitTypeOptions,
  clinicians,
  duplicateClinicianNames,
  onCreateVisit,
}: CalendarCreateVisitDialogProps) => {
  const visitTypeFieldId = useId();
  const clinicianFieldId = useId();
  const [selectedVisitType, setSelectedVisitType] = useState<VisitTypeId | ''>(
    ''
  );
  const [selectedClinicianId, setSelectedClinicianId] = useState('');
  const [isCreatingVisit, setIsCreatingVisit] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [taskTypeFilter, setTaskTypeFilter] = useState<TaskTypeFilterValue>(
    () => createDefaultTaskTypeFilter()
  );

  const resetDialogState = useCallback(() => {
    setSelectedVisitType('');
    setSelectedClinicianId('');
    setCreateError(null);
    setTaskTypeFilter(createDefaultTaskTypeFilter());
  }, []);

  useEffect(() => {
    if (!open) {
      resetDialogState();
      setIsCreatingVisit(false);
    }
  }, [open, resetDialogState]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isCreatingVisit) {
      return;
    }

    onOpenChange(nextOpen);
  };

  const handleCreateVisit = async () => {
    if (!selectedDate || !selectedVisitType) {
      return;
    }

    setCreateError(null);
    setIsCreatingVisit(true);
    try {
      const clinicianId =
        selectedClinicianId && selectedClinicianId !== 'none'
          ? selectedClinicianId
          : undefined;
      const createSucceeded = await onCreateVisit(
        selectedDate,
        selectedVisitType,
        clinicianId
      );
      if (!createSucceeded) {
        setCreateError('Failed to create task. Please try again.');
        return;
      }
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating task:', error);
      setCreateError('Failed to create task. Please try again.');
    } finally {
      setIsCreatingVisit(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Task</DialogTitle>
          <DialogDescription className='sr-only'>
            Select a task type and optional clinician for this calendar date.
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-4'>
          <div>
            <p className='text-sm font-medium'>Date</p>
            <div className='mt-1 rounded bg-gray-50 p-2 text-sm'>
              {selectedDate?.toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </div>
          </div>

          <div>
            <label className='text-sm font-medium' htmlFor={visitTypeFieldId}>
              Task Type
            </label>
            <div className='mt-1'>
              <TaskTypeCombobox
                id={visitTypeFieldId}
                options={visitTypeOptions}
                value={selectedVisitType}
                onValueChange={setSelectedVisitType}
                filter={taskTypeFilter}
                onFilterChange={setTaskTypeFilter}
                disabled={isCreatingVisit}
                placeholder='Select task type'
              />
            </div>
          </div>

          <div>
            <label className='text-sm font-medium' htmlFor={clinicianFieldId}>
              Assigned Clinician
            </label>
            <Select
              value={selectedClinicianId}
              onValueChange={setSelectedClinicianId}
              disabled={isCreatingVisit}
            >
              <SelectTrigger id={clinicianFieldId} className='mt-1'>
                <SelectValue placeholder='Select clinician (optional)' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='none'>No assignment</SelectItem>
                {clinicians.map(clinician => {
                  const displayName = getClinicianDisplayName(clinician);
                  return (
                    <SelectItem key={clinician.id} value={clinician.id}>
                      {displayName}
                      {duplicateClinicianNames?.has(displayName) &&
                        clinician.user_email && (
                          <span className='ml-1 text-xs text-muted-foreground'>
                            ({clinician.user_email})
                          </span>
                        )}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {createError && (
            <p
              role='alert'
              className='rounded border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive'
            >
              {createError}
            </p>
          )}

          <div className='flex gap-2 pt-4'>
            <Button
              onClick={handleCreateVisit}
              disabled={!selectedDate || !selectedVisitType || isCreatingVisit}
              className='flex-1'
            >
              {isCreatingVisit ? (
                <>
                  <div className='h-4 w-4 animate-spin rounded-full border-b-2 border-white' />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className='h-4 w-4' />
                  Create Task
                </>
              )}
            </Button>
            <Button
              variant='outline'
              onClick={() => handleOpenChange(false)}
              disabled={isCreatingVisit}
              className='flex-1'
            >
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CalendarCreateVisitDialog;
